import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Phone, Mail, MapPin } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import SEO from '../components/SEO';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    reason: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Send form data to backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        toast({
          title: "Message envoyé !",
          description: "Nous vous répondrons dans les 24 heures.",
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          reason: '',
          message: ''
        });
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive"
      });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="pt-20">
      <SEO
        title="Contact — CarMaintenance Genève | 022 980 93 93"
        description="Une question sur votre abonnement automobile ou notre garage à Meyrin ? Contactez l'équipe CarMaintenance par téléphone, e-mail ou formulaire. Réponse sous 24 h."
        path="/contact"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          mainEntity: {
            '@type': 'Organization',
            name: 'CarMaintenance',
            telephone: '+41229809393',
            email: 'info@car-main.ch',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Rue des Lattes 62',
              addressLocality: 'Meyrin',
              postalCode: '1217',
              addressRegion: 'GE',
              addressCountry: 'CH',
            },
          },
        }}
      />
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#e8f5ef] via-[#f0f9f4] to-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 animate-in fade-in slide-in-from-bottom duration-700">
            Besoin d'aide?
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            N'hésitez pas à nous contacter en utilisant les options ci-dessous, et notre équipe dédiée répondra rapidement à vos demandes.
          </p>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Connectons<span className="text-[#52a675]">-nous</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Contactez-nous pour en savoir plus sur nos programmes ou pour partager vos idées et votre soutien à notre mission.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <a
              href="tel:0229809393"
              className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group text-center"
            >
              <div className="w-16 h-16 bg-[#52a675]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#52a675]/20 transition-colors">
                <Phone className="w-8 h-8 text-[#52a675]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Téléphone</h3>
              <p className="text-gray-600">022 980 93 93</p>
            </a>

            <a
              href="mailto:info@car-main.ch"
              className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group text-center min-w-0"
            >
              <div className="w-16 h-16 bg-[#52a675]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#52a675]/20 transition-colors">
                <Mail className="w-8 h-8 text-[#52a675]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Adresse E-mail</h3>
              <p className="text-gray-600 break-all">info@car-main.ch</p>
            </a>

            <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#52a675]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-[#52a675]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Adresse</h3>
              <p className="text-gray-600">Rue des Lattes 62</p>
              <p className="text-gray-600">1217 Meyrin, GE</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Restons en <span className="text-[#52a675]">contact</span>
              </h2>
              <p className="text-gray-600">
                Dites-nous qui vous êtes et ce que vous recherchez ci-dessous, et nous vous répondrons dans les 24 heures.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Nom</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Votre nom complet"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="votre.email@exemple.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Raison de nous contacter</Label>
                <Select
                  value={formData.reason}
                  onValueChange={(value) => setFormData({ ...formData, reason: value })}
                  required
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Sélectionnez une raison" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="devis">Obtenir un devis</SelectItem>
                    <SelectItem value="question">Question</SelectItem>
                    <SelectItem value="partenaire">Devenir partenaire</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Votre message..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full"
                />
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full bg-[#52a675] hover:bg-[#3d8a5c] text-white py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Envoyer
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* WhatsApp Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto bg-gradient-to-br from-[#25D366]/10 to-[#25D366]/5 rounded-3xl p-8 border border-[#25D366]/20">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Besoin d'une réponse rapide ?
            </h3>
            <p className="text-gray-600 mb-6">
              Contactez-nous directement sur WhatsApp pour une assistance immédiate.
            </p>
            <a
              href="https://wa.me/41782662662"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="lg"
                className="bg-[#25D366] hover:bg-[#20BA5A] text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <svg
                  className="w-6 h-6 mr-2"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
                Contactez-nous sur WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
